import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Upload, 
  Send, 
  Loader2, 
  Download, 
  Image as ImageIcon,
  FileText,
  History,
  ChevronLeft,
  Sparkles,
  Settings,
  Video,
  Play,
  Camera,
  Building2,
  Plane,
  Wand2,
  Grid3x3,
  GitCompareArrows,
  X,
  LogOut,
  Trash2,
  Menu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import ComparisonSlider from '../components/ComparisonSlider';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ChatApp.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe JSON parser — reads body once, handles all error cases
const safeJson = async (res) => {
  let text;
  try {
    text = await res.text();
  } catch {
    throw new Error('Connection error — server may be unavailable. Please try again.');
  }
  try {
    return JSON.parse(text);
  } catch {
    if (text.includes('Budget has been exceeded')) {
      throw new Error('API budget exceeded. Please add balance in Profile > Universal Key > Add Balance.');
    }
    throw new Error(text.slice(0, 200) || 'Server error — please try again.');
  }
};

const WALKTHROUGH_STYLES = [
  { id: 'cinematic', name: 'Cinematic', icon: Camera, desc: 'Dramatic lighting, slow dolly shots, film grain' },
  { id: 'architectural', name: 'Architectural', icon: Building2, desc: 'Clean visualization, steady camera glide' },
  { id: 'drone', name: 'Drone Flyover', icon: Plane, desc: 'Aerial views, swooping reveals, dynamic angles' },
];

const ChatApp = () => {
  const navigate = useNavigate();
  const { user, logout, authedFetch } = useAuth();
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: 'Welcome to Ezai Build! Upload your 2D building drawings (images, PDF, DWG, or DXF) and I\'ll analyze your floor plan. Then describe your vision and I\'ll generate a 3D render based on your input.',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('Processing...');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [renderId, setRenderId] = useState(null);
  const [renderBase64, setRenderBase64] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [hasAnalysis, setHasAnalysis] = useState(false);
  const [renderCount, setRenderCount] = useState(0);

  // Video state
  const [showVideoPanel, setShowVideoPanel] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [videoTaskId, setVideoTaskId] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);

  const [projectHistory, setProjectHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const [currentProject, setCurrentProject] = useState(null);
  const [showPastRenders, setShowPastRenders] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]); // array of up to 2 history items
  // Mobile UX state
  const [mobileView, setMobileView] = useState('chat'); // 'chat' | 'output'
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Server-side persisted projects (across sessions)
  const [savedProjects, setSavedProjects] = useState([]);
  const [loadingProject, setLoadingProject] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoPollingRef = useRef(null);
  const containerRef = useRef(null);
  const isDraggingDivider = useRef(false);
  const [galleryWidth, setGalleryWidth] = useState(500);

  // Resizable divider logic
  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingDivider.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingDivider.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;
      const clamped = Math.min(Math.max(newWidth, 280), containerRect.width - 350);
      setGalleryWidth(clamped);
    };
    const handleMouseUp = () => {
      if (isDraggingDivider.current) {
        isDraggingDivider.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll video status
  const pollVideoStatus = useCallback(async (taskId) => {
    try {
      const res = await authedFetch(`/api/video/status/${taskId}`);
      const data = await safeJson(res);
      setVideoStatus(data);

      if (data.status === 'completed') {
        setVideoUrl(`${API_URL}${data.video_url}`);
        setIsGeneratingVideo(false);
        clearInterval(videoPollingRef.current);

        setMessages(prev => [...prev, {
          id: prev.length + 1,
          role: 'assistant',
          content: 'Your walkthrough video is ready! Check the video panel on the right to preview and download it.',
          timestamp: new Date(),
        }]);
      } else if (data.status === 'failed') {
        setIsGeneratingVideo(false);
        clearInterval(videoPollingRef.current);

        setMessages(prev => [...prev, {
          id: prev.length + 1,
          role: 'assistant',
          content: `Video generation failed: ${data.error || 'Unknown error'}. Please try again.`,
          timestamp: new Date(),
        }]);
      }
    } catch {
      // Silently retry on network errors
    }
  }, []);

  useEffect(() => {
    return () => {
      if (videoPollingRef.current) clearInterval(videoPollingRef.current);
    };
  }, []);

  // Load saved projects (per-user, server-side)
  const loadSavedProjects = useCallback(async () => {
    try {
      const res = await authedFetch('/api/projects');
      if (!res.ok) return;
      const data = await safeJson(res);
      setSavedProjects(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [authedFetch]);

  useEffect(() => {
    if (user) loadSavedProjects();
  }, [user, loadSavedProjects]);

  // Restore a full project (chat + renders + comparison) when sidebar item is clicked
  const restoreProject = async (sid) => {
    setLoadingProject(true);
    try {
      const res = await authedFetch(`/api/projects/${sid}`);
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Could not load project');

      // Rehydrate messages
      const restoredMsgs = (data.messages || []).map((m, idx) => ({
        id: idx + 1,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        isFile: !!m.is_file,
        filePreview: m.is_file ? data.file_preview : null,
      }));
      setMessages(restoredMsgs.length ? restoredMsgs : [{
        id: 1,
        role: 'assistant',
        content: 'Project restored. Continue describing your vision to create another render.',
        timestamp: new Date(),
      }]);

      setSessionId(sid);
      setUploadPreview(data.file_preview);
      setHasAnalysis(true);

      // Rehydrate renders
      const renders = data.renders || [];
      const rebuiltHistory = renders.slice().reverse().map((r) => ({
        id: r.render_id,
        renderId: r.render_id,
        renderNumber: r.render_number,
        name: (r.description || '').slice(0, 40) + ((r.description || '').length > 40 ? '...' : ''),
        description: r.description,
        date: r.created_at ? new Date(r.created_at).toLocaleDateString() : '',
        thumbnail: `data:image/png;base64,${r.image_base64}`,
        rendered: `data:image/png;base64,${r.image_base64}`,
        original: data.file_preview,
        base64: r.image_base64,
      }));
      setProjectHistory(rebuiltHistory);
      setRenderCount(renders.length);

      if (renders.length) {
        const latest = renders[renders.length - 1];
        setCurrentProject({
          name: `Render #${latest.render_number}`,
          original: data.file_preview,
          rendered: `data:image/png;base64,${latest.image_base64}`,
          date: new Date(),
        });
        setRenderBase64(latest.image_base64);
        setRenderId(latest.render_id);
        setShowComparison(true);
      } else {
        setShowComparison(false);
      }
      setShowPastRenders(false);
      setCompareMode(false);
      setCompareSelection([]);
    } catch (err) {
      console.error('Restore failed:', err);
    } finally {
      setLoadingProject(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setIsProcessing(true);

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const isCad = ext === 'dwg' || ext === 'dxf';
    setProcessingMessage(isCad ? `Analysing file…` : 'Analyzing your floor plan...');

    // For raster files we can show a local preview immediately.
    // For CAD files we wait for the backend conversion so the chat reflects what Claude actually sees.
    const localPreview = isCad ? null : URL.createObjectURL(file);
    const userMessageId = Date.now();
    setMessages(prev => [...prev, {
      id: userMessageId,
      role: 'user',
      content: `Uploaded: ${file.name}`,
      timestamp: new Date(),
      isFile: true,
      filePreview: localPreview,
      isConverting: isCad,
      originalFormat: ext.toUpperCase(),
    }]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (sessionId) formData.append('session_id', sessionId);

      const res = await authedFetch(`/api/chat/upload-and-analyze`, {
        method: 'POST',
        body: formData,
      });

      const data = await safeJson(res);

      if (!res.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      setSessionId(data.session_id);
      setUploadPreview(data.file_preview);
      setHasAnalysis(true);

      // Update the user's file message with the converted preview (backend returns PNG data URL for CAD files)
      setMessages(prev => prev.map(m =>
        m.id === userMessageId
          ? { ...m, filePreview: data.file_preview, isConverting: false }
          : m
      ));

      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        role: 'assistant',
        content: data.analysis + '\n\nNow describe your vision — materials, style, perspective, mood — and I\'ll generate a 3D render based on your input.',
        timestamp: new Date(),
      }]);

      // Refresh sidebar history
      loadSavedProjects();
    } catch (err) {
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        role: 'assistant',
        content: `Error: ${err.message}. Please try uploading again.`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('Processing...');
    }
  };

  const handleGenerate3D = async () => {
    if (!inputValue.trim() || !sessionId) return;

    const userDescription = inputValue;
    setInputValue('');
    setIsProcessing(true);
    setProcessingMessage('Generating your 3D visualization...');

    setMessages(prev => [...prev, {
      id: prev.length + 1,
      role: 'user',
      content: userDescription,
      timestamp: new Date(),
    }]);

    try {
      const params = new URLSearchParams({
        session_id: sessionId,
        description: userDescription,
      });
      if (renderId) params.append('render_id', renderId);

      const res = await authedFetch(`/api/render/generate?${params}`, {
        method: 'POST',
      });

      const data = await safeJson(res);

      if (!res.ok) {
        throw new Error(data.detail || 'Render generation failed');
      }

      const imageDataUrl = `data:image/png;base64,${data.image_base64}`;
      setRenderId(data.render_id);
      setRenderBase64(data.image_base64);
      setShowComparison(true);
      setRenderCount(prev => prev + 1);

      setCurrentProject({
        name: `Render #${renderCount + 1}`,
        original: uploadPreview,
        rendered: imageDataUrl,
        date: new Date(),
      });

      setMessages(prev => [...prev, {
        id: prev.length + 1,
        role: 'assistant',
        content: `Your 3D render is ready! Check the output panel. You can describe a different vision to generate another render, or create a walkthrough video.`,
        timestamp: new Date(),
      }]);

      setProjectHistory(prev => {
        const existing = prev.find(p => p.renderId === data.render_id);
        if (existing) return prev;
        return [{
          id: Date.now(),
          renderId: data.render_id,
          renderNumber: renderCount + 1,
          name: userDescription.slice(0, 40) + (userDescription.length > 40 ? '...' : ''),
          description: userDescription,
          date: 'Just now',
          thumbnail: imageDataUrl,
          rendered: imageDataUrl,
          original: uploadPreview,
          base64: data.image_base64,
        }, ...prev];
      });

      // Refresh sidebar history after each new render
      loadSavedProjects();

    } catch (err) {
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        role: 'assistant',
        content: `Error generating render: ${err.message}. Please try again.`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('Processing...');
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const text = inputValue;
    setInputValue('');
    setIsProcessing(true);
    setProcessingMessage('Thinking...');

    setMessages(prev => [...prev, {
      id: prev.length + 1,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }]);

    try {
      const formData = new FormData();
      if (sessionId) formData.append('session_id', sessionId);
      formData.append('message', text);

      const res = await authedFetch(`/api/chat/message`, {
        method: 'POST',
        body: formData,
      });

      const data = await safeJson(res);

      if (!res.ok) {
        throw new Error(data.detail || 'Message failed');
      }

      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
      }
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        role: 'assistant',
        content: data.content,
        timestamp: new Date(),
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        role: 'assistant',
        content: `Error: ${err.message}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('Processing...');
    }
  };

  const handleGenerateVideo = async () => {
    // Walkthrough video feature is temporarily disabled pending finalization.
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'assistant',
      content: `🎬 Walkthrough video generation is coming soon! We're polishing the experience — stay tuned.`,
      timestamp: new Date(),
    }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDownloadRender = () => {
    if (!renderBase64) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${renderBase64}`;
    link.download = 'ez-build-ai-render.png';
    link.click();
  };

  const handleDownloadVideo = () => {
    if (!videoUrl) return;
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = 'ez-build-ai-walkthrough.mp4';
    link.target = '_blank';
    link.click();
  };

  const handleNewProject = () => {
    setMessages([{
      id: 1,
      role: 'assistant',
      content: 'Welcome back! Upload a new 2D building drawing to get started.',
      timestamp: new Date(),
    }]);
    setUploadedFile(null);
    setShowComparison(false);
    setCurrentProject(null);
    setSessionId(null);
    setRenderId(null);
    setRenderBase64(null);
    setUploadPreview(null);
    setHasAnalysis(false);
    setRenderCount(0);
    setShowVideoPanel(false);
    setSelectedStyle(null);
    setVideoTaskId(null);
    setVideoStatus(null);
    setVideoUrl(null);
    setIsGeneratingVideo(false);
    if (videoPollingRef.current) clearInterval(videoPollingRef.current);
  };

  return (
    <div className="chat-app" data-testid="chat-app">
      {/* Header */}
      <header className="chat-app-header">
        <div className="header-left">
          <Button 
            className="btn-secondary mobile-menu-btn"
            onClick={() => setMobileSidebarOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open history"
          >
            <Menu size={20} />
          </Button>
          <Button 
            className="btn-secondary back-btn"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ChevronLeft size={20} />
            <span className="back-btn-text">Back</span>
          </Button>
          <div className="logo-container">
            <img
              src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
              alt="Ezai Build"
              className="logo-icon"
              style={{ width: 36, height: 36 }}
            />
            <span className="logo-text">Ezai Build</span>
          </div>
        </div>
        <div className="header-right">
          {user && (
            <div className="user-chip" data-testid="user-chip">
              {user.picture ? (
                <img src={user.picture} alt={user.name} />
              ) : (
                <div className="user-avatar-fallback">{(user.name || user.email).charAt(0).toUpperCase()}</div>
              )}
              <span className="body-small user-name">{user.name}</span>
            </div>
          )}
          <Button className="btn-secondary new-project-btn" onClick={handleNewProject} data-testid="new-project-btn">
            <span className="new-project-btn-text">New Project</span>
            <span className="new-project-btn-icon" aria-hidden="true">+</span>
          </Button>
          <Button
            className="btn-secondary icon-btn"
            onClick={async () => { await logout(); navigate('/login'); }}
            data-testid="logout-btn"
            title="Sign out"
          >
            <LogOut size={20} />
          </Button>
        </div>
      </header>

      {/* Mobile view switcher */}
      {hasAnalysis && (
        <div className="mobile-view-tabs" data-testid="mobile-view-tabs">
          <button
            className={`mobile-view-tab ${mobileView === 'chat' ? 'active' : ''}`}
            onClick={() => setMobileView('chat')}
            data-testid="mobile-tab-chat"
          >
            Chat
          </button>
          <button
            className={`mobile-view-tab ${mobileView === 'output' ? 'active' : ''}`}
            onClick={() => setMobileView('output')}
            data-testid="mobile-tab-output"
          >
            Output {projectHistory.length > 0 && `(${projectHistory.length})`}
          </button>
        </div>
      )}

      <div className={`chat-app-container mv-${mobileView}`} ref={containerRef}>
        {/* Mobile sidebar backdrop */}
        {mobileSidebarOpen && (
          <div
            className="mobile-sidebar-backdrop"
            onClick={() => setMobileSidebarOpen(false)}
            data-testid="mobile-sidebar-backdrop"
          />
        )}
        {/* History Sidebar */}
        {showHistory && (
          <aside className={`history-sidebar ${mobileSidebarOpen ? 'open' : ''}`} data-testid="history-sidebar">
            <div className="sidebar-header">
              <History size={20} />
              <h3 className="heading-3">Project History</h3>
              <button
                className="sidebar-close-btn"
                onClick={() => setMobileSidebarOpen(false)}
                data-testid="sidebar-close-btn"
                aria-label="Close history"
              >
                <X size={18} />
              </button>
            </div>
            <div className="history-list">
              {loadingProject && (
                <div style={{ padding: '12px', textAlign: 'center' }}>
                  <Loader2 size={18} className="spinner" />
                </div>
              )}
              {!loadingProject && savedProjects.length === 0 && (
                <p className="body-small" style={{ color: 'var(--text-muted)', padding: '12px', textAlign: 'center' }}>
                  No projects yet. Upload a floor plan to begin.
                </p>
              )}
              {savedProjects.map((project) => (
                <button
                  key={project.session_id}
                  className={`history-item ${sessionId === project.session_id ? 'active' : ''}`}
                  onClick={() => { restoreProject(project.session_id); setMobileSidebarOpen(false); }}
                  data-testid={`history-item-${project.session_id}`}
                  disabled={loadingProject}
                >
                  {project.thumbnail ? (
                    <img src={project.thumbnail} alt={project.file_name || 'Project'} />
                  ) : (
                    <div className="history-thumb-placeholder">
                      <FileText size={20} />
                    </div>
                  )}
                  <div className="history-info">
                    <p className="body-medium history-name">
                      {project.last_description?.slice(0, 38) || project.file_name || 'Untitled project'}
                    </p>
                    <span className="body-small history-meta">
                      {project.render_count} render{project.render_count === 1 ? '' : 's'}
                      {project.is_cad ? ` · ${project.original_format?.toUpperCase()}` : ''}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </aside>
        )}

        {/* Chat Section */}
        <div className="chat-section">
          <div className="chat-messages" data-testid="chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={`message message-${message.role}`}>
                <div className="message-content">
                  {message.isFile ? (
                    <div className="file-upload-message">
                      <div className="file-preview">
                        {message.isConverting ? (
                          <div className="converting-placeholder" data-testid="converting-placeholder">
                            <Loader2 size={28} className="spinner" />
                            <span className="body-small" style={{ marginTop: '8px', color: 'var(--text-muted)' }}>
                              Analysing file…
                            </span>
                          </div>
                        ) : (
                          message.filePreview && <img src={message.filePreview} alt="Floor plan" />
                        )}
                        {message.convertedLabel && (
                          <span className="converted-badge" data-testid="converted-badge">{message.convertedLabel}</span>
                        )}
                      </div>
                      <div className="file-info">
                        <FileText size={20} />
                        <span className="body-medium">{message.content}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="body-medium" style={{ whiteSpace: 'pre-wrap' }}>{message.content}</p>
                  )}
                </div>
                <span className="message-time body-small">
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            ))}
            
            {isProcessing && (
              <div className="message message-assistant">
                <div className="message-content processing">
                  <Loader2 size={20} className="spinner" />
                  <span className="body-medium">{processingMessage}</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".png,.jpg,.jpeg,.pdf,.webp,.dwg,.dxf"
              style={{ display: 'none' }}
            />
            <Button
              className="btn-secondary upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
              data-testid="upload-btn"
            >
              <Upload size={20} />
            </Button>
            <input
              type="text"
              className="chat-input"
              placeholder={hasAnalysis ? "Describe your 3D vision — materials, style, view angle..." : "Upload a floor plan or ask a question..."}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isProcessing}
              data-testid="chat-input"
            />
            {hasAnalysis && inputValue.trim() ? (
              <Button
                className="btn-primary generate-btn"
                onClick={handleGenerate3D}
                disabled={isProcessing}
                data-testid="generate-3d-btn"
              >
                <Wand2 size={20} />
                <span className="generate-btn-text">Generate 3D</span>
              </Button>
            ) : (
              <Button
                className="btn-primary send-btn"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isProcessing}
                data-testid="send-btn"
              >
                <Send size={20} />
              </Button>
            )}
          </div>
          <p className="ai-disclaimer" data-testid="ai-disclaimer">
            Ezai Build is an AI and can make mistakes. Verify important details
          </p>
        </div>

        {/* Resizable Divider + Right Panel */}
        {showComparison && currentProject && (
          <>
            <div
              className="panel-divider"
              onMouseDown={handleDividerMouseDown}
              data-testid="panel-divider"
            />
            <div className="comparison-gallery" style={{ width: galleryWidth }} data-testid="comparison-gallery">
              <div className="gallery-header">
                <div className="gallery-title">
                  <ImageIcon size={20} />
                  <h3 className="heading-3">Output</h3>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {projectHistory.length > 1 && !showVideoPanel && (
                    <Button
                      className={`btn-secondary ${showPastRenders ? 'active' : ''}`}
                      onClick={() => {
                        setShowPastRenders(!showPastRenders);
                        if (showPastRenders) { setCompareMode(false); setCompareSelection([]); }
                      }}
                      data-testid="toggle-past-renders-btn"
                    >
                      <Grid3x3 size={18} />
                      {showPastRenders ? 'Current' : `All (${projectHistory.length})`}
                    </Button>
                  )}
                  {showPastRenders && projectHistory.length >= 2 && (
                    <Button
                      className={`btn-secondary ${compareMode ? 'active' : ''}`}
                      onClick={() => {
                        setCompareMode(!compareMode);
                        setCompareSelection([]);
                      }}
                      data-testid="toggle-compare-mode-btn"
                    >
                      <GitCompareArrows size={18} />
                      {compareMode ? 'Cancel' : 'Compare 2'}
                    </Button>
                  )}
                  <Button
                    className="btn-secondary"
                    onClick={() => { setShowVideoPanel(!showVideoPanel); setShowPastRenders(false); setCompareMode(false); setCompareSelection([]); }}
                    data-testid="toggle-video-btn"
                  >
                    <Video size={18} />
                    {showVideoPanel ? 'Comparison' : 'Walkthrough'}
                  </Button>
                  <Button className="btn-primary" onClick={handleDownloadRender} data-testid="download-render-btn">
                    <Download size={18} />
                  </Button>
                </div>
              </div>
              
              {showPastRenders && !showVideoPanel ? (
                compareMode && compareSelection.length === 2 ? (
                  <div className="compare-two-view" data-testid="compare-two-view">
                    <div className="compare-two-toolbar">
                      <div className="compare-two-labels">
                        <span className="body-small compare-label-left">#{compareSelection[0].renderNumber}</span>
                        <span className="body-small" style={{ color: 'var(--text-muted)' }}>vs</span>
                        <span className="body-small compare-label-right">#{compareSelection[1].renderNumber}</span>
                      </div>
                      <Button
                        className="btn-secondary"
                        onClick={() => setCompareSelection([])}
                        data-testid="compare-reset-btn"
                      >
                        <X size={16} />
                        Reset
                      </Button>
                    </div>
                    <div className="compare-two-slider">
                      <ComparisonSlider
                        beforeImage={compareSelection[1].rendered}
                        afterImage={compareSelection[0].rendered}
                      />
                    </div>
                    <div className="compare-two-captions">
                      <p className="body-small">{compareSelection[0].name}</p>
                      <p className="body-small" style={{ textAlign: 'right' }}>{compareSelection[1].name}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {compareMode && (
                      <div className="compare-hint" data-testid="compare-hint">
                        <p className="body-small">
                          {compareSelection.length === 0 && 'Select 2 renders to compare side-by-side'}
                          {compareSelection.length === 1 && 'Pick one more render…'}
                        </p>
                      </div>
                    )}
                    <div className="past-renders-grid" data-testid="past-renders-grid">
                      {projectHistory.map((p) => {
                        const isSelected = compareSelection.some(s => s.id === p.id);
                        const selectionIndex = compareSelection.findIndex(s => s.id === p.id);
                        return (
                          <button
                            key={p.id}
                            className={`past-render-card ${!compareMode && currentProject?.rendered === p.rendered ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              if (compareMode) {
                                setCompareSelection(prev => {
                                  if (prev.some(s => s.id === p.id)) {
                                    return prev.filter(s => s.id !== p.id);
                                  }
                                  if (prev.length >= 2) return prev; // cap at 2
                                  return [...prev, p];
                                });
                              } else {
                                setCurrentProject({
                                  name: `Render #${p.renderNumber}`,
                                  original: p.original,
                                  rendered: p.rendered,
                                  date: new Date(),
                                });
                                setRenderBase64(p.base64);
                                setRenderId(p.renderId);
                                setShowPastRenders(false);
                              }
                            }}
                            data-testid={`past-render-${p.renderNumber}`}
                          >
                            {isSelected && (
                              <span className="selection-badge" data-testid={`selection-badge-${p.renderNumber}`}>
                                {selectionIndex + 1}
                              </span>
                            )}
                            <img src={p.thumbnail} alt={p.name} />
                            <div className="past-render-meta">
                              <span className="body-small past-render-num">#{p.renderNumber}</span>
                              <span className="body-small past-render-name">{p.name}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )
              ) : !showVideoPanel ? (
                <>
                  <div className="comparison-container">
                    <ComparisonSlider
                      beforeImage={currentProject.rendered}
                      afterImage={currentProject.original}
                    />
                  </div>
                  <div className="gallery-info">
                    <div className="info-item">
                      <span className="body-small">Render:</span>
                      <span className="body-medium">#{renderCount}</span>
                    </div>
                    <div className="info-item">
                      <span className="body-small">Generated:</span>
                      <span className="body-medium">Just now</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="video-panel" data-testid="video-panel">
                  <div className="video-style-selector">
                    <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '12px' }}>
                      Choose walkthrough style:
                    </p>
                    <div className="style-grid">
                      {WALKTHROUGH_STYLES.map((style) => {
                        const Icon = style.icon;
                        return (
                          <button
                            key={style.id}
                            className={`style-card ${selectedStyle?.id === style.id ? 'selected' : ''}`}
                            onClick={() => setSelectedStyle(style)}
                            disabled={isGeneratingVideo}
                            data-testid={`style-${style.id}`}
                          >
                            <Icon size={24} className="style-icon" />
                            <span className="body-medium">{style.name}</span>
                            <span className="body-small style-desc">{style.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Button
                    className="btn-primary generate-video-btn"
                    onClick={handleGenerateVideo}
                    disabled={!selectedStyle || isGeneratingVideo}
                    data-testid="generate-video-btn"
                  >
                    {isGeneratingVideo ? (
                      <>
                        <Loader2 size={18} className="spinner" />
                        {videoStatus?.progress_message || 'Generating...'}
                      </>
                    ) : (
                      <>
                        <Play size={18} />
                        Generate Walkthrough
                      </>
                    )}
                  </Button>

                  {isGeneratingVideo && (
                    <div className="video-progress" data-testid="video-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" />
                      </div>
                      <p className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
                        {videoStatus?.progress_message || 'This typically takes 2-5 minutes...'}
                      </p>
                    </div>
                  )}

                  {videoUrl && (
                    <div className="video-player-container" data-testid="video-player">
                      <video 
                        controls 
                        className="video-player"
                        src={videoUrl}
                        autoPlay
                      >
                        Your browser does not support the video tag.
                      </video>
                      <Button
                        className="btn-primary"
                        onClick={handleDownloadVideo}
                        style={{ marginTop: '12px', width: '100%' }}
                        data-testid="download-video-btn"
                      >
                        <Download size={18} />
                        Download Video
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatApp;
